/**
 * The API's attachment → what the chip, the tiles and the viewer show: absolute links on the regional API,
 * the salon's local time, sizes and lengths ready to read.
 */
import { DateTime } from "luxon"
import { resolveMediaUrl } from "@/lib/resolveMediaUrl"
import { getCompanyRegistry } from "@/lib/company/companyRegistrySingleton"
import type { ApiAttachment } from "@/lib/services/attachmentsService"
import type { AttachmentItem } from "./types"

/** A grey tile with a camera, for a video whose poster frame is not made yet */
export const VIDEO_PLACEHOLDER =
  "data:image/svg+xml;utf8," +
  encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" fill="#e5e7eb"/>' +
    '<path d="M18 24h20a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H18a3 3 0 0 1-3-3V27a3 3 0 0 1 3-3zm24 6 8-5v14l-8-5z" fill="#9ca3af"/></svg>')

export function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

export function formatDuration(seconds: number): string {
  const s = Math.round(seconds)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`
}

export function localWhen(iso: string | null | undefined, format = "dd.MM HH:mm"): string {
  if (!iso) return ""
  const tz = getCompanyRegistry().getTimezone?.() ?? "UTC"
  const dt = DateTime.fromISO(iso, { zone: "utc" })
  return dt.isValid ? dt.setZone(tz).toFormat(format) : ""
}

export function toItem(a: ApiAttachment): AttachmentItem {
  return {
    id: String(a.id),
    kind: a.kind,
    status: a.status,
    src: a.url ? resolveMediaUrl(a.url) : "",
    downloadSrc: a.download_url ? resolveMediaUrl(a.download_url) : undefined,
    thumb: a.thumb_url ? resolveMediaUrl(a.thumb_url) : VIDEO_PLACEHOLDER,
    by: a.by ?? "",
    when: localWhen(a.created_at),
    size: a.size ? formatSize(a.size) : undefined,
    duration: a.duration ? formatDuration(a.duration) : undefined,
    processing: a.status === "processing" || a.status === "uploading",
    failed: a.status === "failed",
    canDelete: a.can_delete,
  }
}
